// holds state for details page
import { ActionType, getType } from "typesafe-actions";
import { useTypesafeActions } from "store";
import React, { useContext, useEffect, useState } from "react";
import {
  CategoryActions,
  CategoryActionTypes,
  CategoryName,
  RootActions,
  RootActionTypes,
  RootState,
  SharedCategoryState,
  Tab
} from "./userGroupDetailsTypes";
import { UserGroupDetailsLoader } from "./UserGroupDetailsLoader";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";
import { useHistory } from "polyrouter";

// Reducer handling state updates for individual categories.
// To limit code duplication, we pass in what category the
// reducer will update. This allows us to define the same reducer
// for different state objects of the same type.
function createCategoryReducer(category: CategoryName) {
  return function categoryReducer(
    state: SharedCategoryState,
    a: ActionType<CategoryActionTypes> & { category: CategoryName }
  ): SharedCategoryState {
    // Only update the desired category
    if (a.category !== category) return state;

    switch (a.type) {
      case getType(CategoryActions.updateSelectedItems):
        return {
          ...state,
          selectedItems: a.payload
        };
      case getType(CategoryActions.updateSearchTerm):
        if (state.searchTerm === a.payload) {
          return state;
        }
        return {
          ...state,
          searchTerm: a.payload,
          cursor: null
        };
      case getType(CategoryActions.updateCursor):
        return {
          ...state,
          cursor: a.payload
        };
      case getType(CategoryActions.updatePageLoading):
        return {
          ...state,
          pageLoading: a.payload
        };
      case getType(CategoryActions.updatePageLoadingAndCursor):
        return {
          ...state,
          pageLoading: a.payload.pageLoading,
          cursor: a.payload.cursor
        };
      case getType(CategoryActions.updateActionInProgress):
        return {
          ...state,
          actionInProgress: a.payload
        };
      case getType(CategoryActions.updateActionInProgressAndCursor):
        return {
          ...state,
          actionInProgress: a.payload.actionInProgress,
          cursor: a.payload.cursor
        };
      default:
        return state;
    }
  };
}

// Reducer for state updates for the whole group details page.
// Right now, this only holds what tab we are viewing.
function rootReducer(
  state: RootState,
  a: ActionType<RootActionTypes>
): RootState {
  switch (a.type) {
    case getType(RootActions.updateTab):
      return {
        ...state,
        tab: a.payload
      };
    default:
      return state;
  }
}

export const UserGroupDetailsContainer: React.FC<{
  groupId: string;
}> = ({ groupId }) => {
  const { tenant } = useContext(SelectedTenantContext);
  const history = useHistory();

  // Mechanism for tracking changes in tenant selection.
  // If our current tenant changes (from something other than the initial 'undefined'),
  // navigate back to the user group list page.
  const [currentTenantId, setCurrentTenantId] = useState<string | undefined>(
    undefined
  );
  useEffect(() => {
    if (!currentTenantId && tenant?.id) {
      setCurrentTenantId(tenant.id);
      return;
    }
    if (tenant?.id !== currentTenantId) {
      history.push("/manage/device-users/groups");
    }
  }, [tenant, history, currentTenantId]);

  const initialRoot: RootState = {
    tab: Tab.GROUPED_USERS
  };

  const initialGroupedUserCategory: SharedCategoryState = {
    cursor: null,
    pageLoading: false,
    selectedItems: [],
    actionInProgress: false
  };

  const initialAvailableUsersCategory: SharedCategoryState = {
    cursor: null,
    pageLoading: false,
    selectedItems: [],
    actionInProgress: false
  };

  const [rootState, rootStateUpdate] = useTypesafeActions(
    rootReducer,
    initialRoot,
    RootActions
  );

  const groupedUsersCategoryReducer = createCategoryReducer(
    CategoryName.GROUPED_USERS
  );
  const [groupedState, groupedUpdate] = useTypesafeActions(
    groupedUsersCategoryReducer,
    initialGroupedUserCategory,
    CategoryActions
  );

  const availableUsersCategoryReducer = createCategoryReducer(
    CategoryName.AVAILABLE_TO_ADD
  );
  const [availableState, availableUpdate] = useTypesafeActions(
    availableUsersCategoryReducer,
    initialAvailableUsersCategory,
    CategoryActions
  );

  const loaderProps = {
    rootState,
    rootStateUpdate,
    availableState,
    availableUpdate,
    groupedState,
    groupedUpdate,
    groupId
  };

  return (
    <>
      {tenant?.id && (
        <UserGroupDetailsLoader tenantId={tenant.id} {...loaderProps} />
      )}
    </>
  );
};
